/**
 * Simple persistent keyval store implementation built on top of IndexedDB, similar to localForage or IDB-KeyVal. This
 * implementation works on IE11 and has proper typings though.
 */
export class KeyValStore {
  readonly dbPromise: Promise<IDBDatabase>;

  constructor(private dbName: string, private storeName: string) {
    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName, 1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(storeName);
      };
    });
  }

  public get<T>(key: IDBValidKey): Promise<T> {
    return this.dbPromise
      .then(db => new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      }));
  }

  public set<T>(key: IDBValidKey, val: T): Promise<void> {
    return this.dbPromise
      .then(db => new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.put(val, key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));
  }

  public delete(key: IDBValidKey): Promise<void> {
    return this.dbPromise
      .then(db => new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));
  }

  public clear(): Promise<void> {
    return this.dbPromise
      .then(db => new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      }));
  }

  public keys(): Promise<IDBValidKey[]> {
    return this.dbPromise
      .then(db => new Promise<IDBValidKey[]>((resolve, reject) => {
        const keys: IDBValidKey[] = [];
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.openKeyCursor();
        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            keys.push(cursor.key);
            cursor.continue();
          } else {
            resolve(keys);
          }
        };
        request.onerror = () => reject(request.error);
      }));
  }
}
